import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Button,
  Fade,
  useTheme,
  useMediaQuery,
  IconButton,
  Link
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Icons
import BuildIcon from '@mui/icons-material/Build';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import StartIcon from '@mui/icons-material/Start';

// Import your logo and images
import logo0 from '../assets/logo0.png';

// Using specific images from the internet that match our functionalities
const aiDiagnosticsImage = 'https://img.freepik.com/free-photo/ai-technology-microchip-background-digital-transformation-concept_53876-124669.jpg';
const smartRepairToolsImage = 'https://img.freepik.com/free-photo/technician-repairing-computer-hardware-electronics-repair-shop_1303-19722.jpg';
const cloudIntegrationImage = 'https://img.freepik.com/free-photo/cloud-computing-technology-with-3d-render-blue-cloud-computing-service-icon_53876-129780.jpg';
const heroBackgroundImage = 'https://img.freepik.com/free-photo/abstract-digital-grid-black-background_53876-97647.jpg';

// Animations
const shine = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 200, 83, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 200, 83, 0.6);
  }
`;

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#111',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  height: '100%',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 10px 30px rgba(255,215,0,0.2)'
  }
}));

const StyledCardMedia = styled(CardMedia)({
  height: 280,
  position: 'relative',
  transition: 'transform 0.5s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%)',
    zIndex: 1
  },
  '&:hover': {
    transform: 'scale(1.05)'
  }
});

const GlowingButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #00C853, #69F0AE)',
  color: '#000',
  padding: '12px 24px',
  border: 'none',
  borderRadius: 0,
  fontWeight: 600,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '200%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    animation: `${shine} 2s infinite linear`,
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    animation: `${glowPulse} 2s infinite`,
  }
}));

const FloatingIcon = styled(Box)(({ theme }) => ({
  animation: `${float} 3s ease-in-out infinite`,
}));

const Footer = () => (
  <Box sx={{
    bgcolor: '#f5f5f5',
    py: 6,
    borderTop: '1px solid rgba(0,0,0,0.1)'
  }}>
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 2 }}>
            <img 
              src={logo0} 
              alt="Logo" 
              style={{ 
                height: '35px',
                filter: 'none',
                opacity: 1
              }} 
            />
          </Box>
          <Typography sx={{ color: '#333', maxWidth: '300px' }}>
            Empowering device repair through AI-driven solutions and expert guidance.
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={4}>
            <Grid item xs={6} md={4}>
              <Typography variant="h6" sx={{ color: '#000', mb: 2, fontWeight: 600 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {['About', 'Careers', 'Contact'].map(link => (
                  <Link
                    key={link}
                    href="#"
                    sx={{
                      color: '#666',
                      textDecoration: 'none',
                      '&:hover': { color: '#000' }
                    }}
                  >
                    {link}
                  </Link>
                ))}
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="h6" sx={{ color: '#000', mb: 2, fontWeight: 600 }}>
                Resources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {['Documentation', 'Support', 'Terms'].map(link => (
                  <Link
                    key={link}
                    href="#"
                    sx={{
                      color: '#666',
                      textDecoration: 'none',
                      '&:hover': { color: '#000' }
                    }}
                  >
                    {link}
                  </Link>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const Adwati = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const tools = [
    {
      id: 1,
      title: 'AI-Powered Diagnostics',
      description: 'Advanced artificial intelligence algorithms analyze device issues in real-time, providing accurate fault detection and solutions.',
      image: aiDiagnosticsImage,
      features: ['Instant Problem Detection', 'Smart Analysis', 'Accurate Results']
    },
    {
      id: 2,
      title: 'Smart Repair Tools',
      description: 'Professional-grade repair tools enhanced with AI capabilities for precise and efficient device repairs.',
      image: smartRepairToolsImage,
      features: ['Guided Repairs', 'Step-by-step Instructions', 'Quality Assurance']
    },
    {
      id: 3,
      title: 'Cloud Integration',
      description: 'Seamless cloud-based solution for storing and accessing repair histories, diagnostics data, and user profiles.',
      image: cloudIntegrationImage,
      features: ['Secure Storage', 'Real-time Sync', 'Data Analytics']
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#000',
      position: 'relative',
      overflow: 'hidden',
      pt: 8,
      pb: 0
    }}>
      {/* Enhanced Background Elements with Hero Image */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${heroBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(0,0,0,0.9) 30%, rgba(0,0,0,0.7) 90%)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '20%',
          left: '-10%',
          width: '120%',
          height: '40%',
          background: 'linear-gradient(90deg, transparent, #FFD700)',
          transform: 'rotate(-12deg)',
          opacity: 0.1,
          zIndex: 1
        }
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Enhanced Header Section */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <img 
              src={logo0} 
              alt="Logo" 
              style={{ 
                height: '60px',
                marginBottom: '20px',
                filter: 'brightness(0) invert(1)',
                opacity: 1
              }} 
            />
          </Box>
          <Typography 
            variant="h2" 
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              color: '#FFD700',
              mb: 3,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textShadow: '0 0 15px rgba(255, 215, 0, 0.5)'
            }}
          >
            Smart Device Repair
          </Typography>
          <Typography 
            variant="h5" 
            sx={{
              color: '#fff',
              maxWidth: '800px',
              mx: 'auto',
              mb: 2,
              opacity: 0.9,
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Our intelligent platform combines AI-powered diagnostics with professional repair tools
            to deliver fast, accurate, and reliable solutions for all your device repair needs.
          </Typography>
          <Typography 
            variant="h6" 
            sx={{
              color: '#00C853',
              maxWidth: '700px',
              mx: 'auto',
              mb: 6,
              fontWeight: 500
            }}
          >
            Trusted by over 10,000+ technicians worldwide
          </Typography>
        </Box>

        {/* Tools Grid */}
        <Grid container spacing={4}>
          {tools.map((tool, index) => (
            <Grid item xs={12} md={4} key={tool.id}>
              <Fade in={isLoaded} timeout={1000} style={{ transitionDelay: `${200 * index}ms` }}>
                <StyledCard>
                  <StyledCardMedia
                    component="img"
                    image={tool.image}
                    alt={tool.title}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      height: 280,
                      filter: 'brightness(0.9) contrast(1.1)'
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{
                      color: '#FFD700',
                      fontWeight: 600,
                      mb: 2
                    }}>
                      {tool.title}
                    </Typography>
                    <Typography sx={{
                      color: '#fff',
                      opacity: 0.9,
                      mb: 3
                    }}>
                      {tool.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {tool.features.map((feature, idx) => (
                        <Typography key={idx} sx={{
                          color: '#00C853',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.5
                        }}>
                          <ArrowForwardIcon sx={{ fontSize: '0.8rem' }} />
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<StartIcon />}
                      sx={{
                        bgcolor: '#FFD700',
                        color: '#000',
                        width: '100%',
                        py: 1.5,
                        fontWeight: 600,
                        borderRadius: 0,
                        textTransform: 'none',
                        fontSize: '1rem',
                        '&:hover': {
                          bgcolor: '#00C853',
                          color: '#fff'
                        }
                      }}
                    >
                      Start Now
                    </Button>
                  </CardContent>
                </StyledCard>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#00C853',
              color: '#000',
              px: 4,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 0,
              '&:hover': {
                bgcolor: '#00E676'
              }
            }}
          >
            Get Started
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Adwati; 